import PlayerScoreTable from "../../players/tables/PlayerScoreTable";
import MatchBox from "../../matches/MatchBox";
import {ROUND_STATES} from "../../../util/Constants";
import {isMatchFinished, sortMatches} from "../../../util/HelperUtils";
import {getLoggedUser} from "../../../util/SecurityUtils";
import {InputGroup, Tab, Tabs} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {useState} from "react";
import Form from "react-bootstrap/Form";
import {useIsMobile} from "../../../hooks/useIsMobile";
import type {Match, TournamentRound} from "../../../types/domain";

interface SwissRoundTabProps {
  round: TournamentRound;
  onMatchClick: (match: Match) => void;
  tournamentId: number;
  canManage: boolean;
}

// Re-orders the unfinished portion so that riskOfDelay matches come right after
// the logged-in player's match (which sortMatches already pinned to the top).
function prioritizeRiskOfDelay(sorted: Match[]): Match[] {
  const playerId = getLoggedUser()?.playerId;
  const isMine = (m: Match) => playerId != null && (m.playerOne.id === playerId || m.playerTwo.id === playerId);
  const unfinished = sorted.filter(m => !isMatchFinished(m.state));
  const finished = sorted.filter(m => isMatchFinished(m.state));
  const mine = unfinished.filter(isMine);
  const others = unfinished.filter(m => !isMine(m));
  const risky = others.filter(m => m.riskOfDelay);
  const rest = others.filter(m => !m.riskOfDelay);
  return [...mine, ...risky, ...rest, ...finished];
}

function SwissRoundTab(props: SwissRoundTabProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [ matchFilter, setMatchFilter ] = useState('')

  const isInProgress = (round: TournamentRound) => round.state === ROUND_STATES.IN_PROGRESS;

  const round = props.round
  const baseSorted = sortMatches(round.matches, matchFilter)
  const sortedMatches = isInProgress(round) ? prioritizeRiskOfDelay(baseSorted) : baseSorted;
  const playedMatches = sortedMatches.filter((m: Match) => isMatchFinished(m.state));
  const unfinishedMatches = sortedMatches.filter((m: Match) => !isMatchFinished(m.state));

  const handleMatchClick = (m: Match) => props.onMatchClick(m);

  const searchInput = (
    <InputGroup className="mt-3 me-5 mb-1">
      <InputGroup.Text id="searchbar" style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>{t("search")}</InputGroup.Text>
      <Form.Control onChange={e => setMatchFilter(e.target.value)} style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }} />
    </InputGroup>
  );

  const scoreTable = (
    <PlayerScoreTable scores={round.scores} tournamentId={props.tournamentId} />
  );

  const matchesSection = isMobile ? (
    <>
      {searchInput}
      {sortedMatches.map(match => (
        <MatchBox key={"match-box-" + match.id} match={match} onClick={() => handleMatchClick(match)} indicateFinished={isInProgress(round)} highlightRiskOfDelay={isInProgress(round)} canManage={props.canManage} />
      ))}
    </>
  ) : (
    <>
      {searchInput}
      <div className="unplayed-match-in-row">
        {unfinishedMatches.map(match => (
          <MatchBox variant="unfinished" key={"match-box-" + match.id} match={match} onClick={() => handleMatchClick(match)} highlightRiskOfDelay={isInProgress(round)} canManage={props.canManage} />
        ))}
      </div>
      {playedMatches.map(match => (
        <MatchBox key={"match-box-" + match.id} match={match} onClick={() => handleMatchClick(match)} indicateFinished={isInProgress(round)} highlightRiskOfDelay={isInProgress(round)} canManage={props.canManage} />
      ))}
    </>
  );

  return (
    <div>
      {isMobile ? (
        <Tabs
          defaultActiveKey={isInProgress(round) ? "matches-tab" : "score-tab"}
          id="mobile-group-tabs"
          variant="tabs"
          fill
          className="theme-tabs"
          style={{ borderColor: 'var(--border-color)' }}
        >
          <Tab eventKey="score-tab" title={t("scoretable")}>{scoreTable}</Tab>
          <Tab eventKey="matches-tab" title={t("matches")}>{matchesSection}</Tab>
        </Tabs>
      ) : (
        <>
          <div className="left-half-responsive">{scoreTable}</div>
          <div className="right-half-responsive">{matchesSection}</div>
        </>
      )}
    </div>
  )
}

export default SwissRoundTab
